import axios from "axios";

const getBaseUrl = () => process.env.REACT_APP_BASE_URL || "https://provider.numente.io";

const getAuthHeaders = () => {
  const token = localStorage.getItem("user_id");
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  };
};

export const fetchConversations = async () => {
  const response = await axios.get(
    `${getBaseUrl()}/chat/conversations`,
    getAuthHeaders()
  );
  return response.data;
};

export const ensureConversation = async (patientUserId) => {
  const response = await axios.post(
    `${getBaseUrl()}/chat/conversations`,
    { patientUserId },
    getAuthHeaders()
  );
  return response.data;
};

export const fetchMessages = async (conversationId, params = {}) => {
  const response = await axios.get(
    `${getBaseUrl()}/chat/conversations/${conversationId}/messages`,
    {
      ...getAuthHeaders(),
      params,
    }
  );
  return response.data;
};

export const sendMessage = async (conversationId, body, clientMessageId) => {
  const response = await axios.post(
    `${getBaseUrl()}/chat/conversations/${conversationId}/messages`,
    { body, clientMessageId },
    getAuthHeaders()
  );
  return response.data;
};

export const markConversationRead = async (conversationId, lastReadMessageId) => {
  const response = await axios.post(
    `${getBaseUrl()}/chat/conversations/${conversationId}/read`,
    { lastReadMessageId },
    getAuthHeaders()
  );
  return response.data;
};

export const fetchUnreadCount = async () => {
  const response = await axios.get(
    `${getBaseUrl()}/chat/unread-count`,
    getAuthHeaders()
  );
  return response.data;
};

export const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

export const formatMessageTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const isSameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  if (isSameDay) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate();

  if (isYesterday) return "Yesterday";

  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

export const formatBubbleTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
};

export const formatDateDivider = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const isSameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  if (isSameDay) return "Today";

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate();

  if (isYesterday) return "Yesterday";

  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const groupMessagesByDate = (messages = []) => {
  const groups = [];

  messages.forEach((message) => {
    const label = formatDateDivider(message.createdAt);
    const lastGroup = groups[groups.length - 1];

    if (!lastGroup || lastGroup.date !== label) {
      groups.push({ date: label, items: [message] });
      return;
    }

    lastGroup.items.push(message);
  });

  return groups;
};

export const getViewerActorId = () => {
  const token = localStorage.getItem("user_id");
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.user_id != null ? String(payload.user_id) : null;
  } catch {
    return null;
  }
};

export const normalizeChatMessage = (
  message,
  viewerActorId = getViewerActorId()
) => ({
  ...message,
  isSelf:
    viewerActorId != null &&
    String(message.senderId) === String(viewerActorId),
});

export const createClientMessageId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};
